import React, {useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import Checkbox from '@material-ui/core/Checkbox'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import {
  checkAvailabilityUpgradeEegSummaryAnalysisApi,
  finalizeEegSummary,
  SearchedIndividualSummary,
  upgradeEegSummaryApi,
} from 'api/analysisApi'
import ActionButton from 'components/atoms/Buttons/ActionButton'
import TableButton from 'components/atoms/Buttons/TableButton'
import {ClientName} from 'components/atoms/ClientName'
import Pagination from 'components/molcules/Pagination'
import TableCell from 'components/Table/TableCell'
import TableCellReport from 'components/Table/TableCellReport'
import TableReportSearch from 'components/Table/TableReportSearch'
import useStyles from 'components/Table/useTableStyles'
import useToolbarStyles from 'components/Table/useTableToolbarStyles'
import {AnalysisReportType} from 'constants/AnalysisConstant'
import {getEegSummaryReportPath} from 'constants/RouteConstant'
import {useAnalysisConfirm} from 'features/analysis/useAnalysisConfirm'
import useEegSummarySearch from 'features/analysis/useEegSummarySearch'
import useFailureModal from 'features/modal/useFailureModal'
import useNoticeConfirm from 'features/modal/useNoticeConfirm'
import useSuccessModal from 'features/modal/useSuccessModal'
import {
  statusToI18nString,
  statusToI18nStringPremium,
} from 'helpers/analysisHelper'
import {isDefined, getQueryFromUrl} from 'helpers/commonHelper'
import {
  dateToPeriodString,
  getDefaultPeriodDate,
  isoStringToDateAndTimeString,
} from 'helpers/dateHelper'
import openInNewTab from 'helpers/openInNewTab'

const isUpdatable = (searched: SearchedIndividualSummary) =>
  searched.progress === 'SUCCESS' && searched.additionalAnalysis === null

interface TableToolbarProps {
  selectedId: number
  isShowSummaryButton: boolean
}

function TableToolbar({selectedId, isShowSummaryButton}: TableToolbarProps) {
  const location = useLocation()
  const {t} = useTranslation()
  const classes = useToolbarStyles()
  const {onOpen: handleSuccessOpen} = useSuccessModal()
  const {onCancel: onAnalysisCancel} = useAnalysisConfirm()
  const {onOpen: handleFailureOpen} = useFailureModal()
  const {onOpen: onNoticeDialogOpen} = useNoticeConfirm()
  const {query, search, onSearch} = useEegSummarySearch()
  const selection = getDefaultPeriodDate()

  // Init search value  from url if exist
  useEffect(() => {
    const initValue = getQueryFromUrl(location.search)
    onSearch({
      ...query,
      period: dateToPeriodString(selection),
      paging: {page: 0, size: 10},
      ...initValue,
    })
  }, [])

  const premiumRequest = async () => {
    try {
      const checkAvailabilityRes =
        await checkAvailabilityUpgradeEegSummaryAnalysisApi(selectedId)
      if (!checkAvailabilityRes.data.available) {
        onAnalysisCancel()
        handleFailureOpen(String(checkAvailabilityRes.data.message))
        return
      }
      await upgradeEegSummaryApi(selectedId)
      handleSuccessOpen(t('IProcessSuccess'))
      onSearch(query)
    } catch (error) {
      onAnalysisCancel()
      handleFailureOpen(error.message)
    }
  }

  const handlePremiumSummary = async () => {
    if (selectedId === 0) {
      handleFailureOpen(t('ISelectRequired'))
      return
    }
    onNoticeDialogOpen({
      title: t('INoticeTitle'),
      message: t('INoticeConfirmDesc'),
    }).then((result) => {
      if (result.payload) premiumRequest()
    })
  }

  return (
    <div className={classes.root}>
      <TableReportSearch
        reportType={AnalysisReportType.EEGSummary}
        isRoot={false}
        query={query}
        search={search}
        onSearch={onSearch}
      />
      {isShowSummaryButton && (
        <div className={classes.containerOptional}>
          <ActionButton onClick={handlePremiumSummary}>
            {t('IPremiumSummary')}
          </ActionButton>
        </div>
      )}
    </div>
  )
}

interface RenderTableRowProps {
  item: SearchedIndividualSummary
  onClick: (item: SearchedIndividualSummary) => void
  isSelected: (item: SearchedIndividualSummary) => boolean
}

function RenderTableRow({item, onClick, isSelected}: RenderTableRowProps) {
  const {t} = useTranslation()

  const checked = isSelected(item)
  const labelId = `summary-table-id-${item.id}`

  const {query, onSearch} = useEegSummarySearch()
  const {onOpen: handleFailureModalOpen} = useFailureModal()

  const handleResultClick = (event: any) => {
    event.stopPropagation()
    openInNewTab(getEegSummaryReportPath(item.id))
  }

  const handleClick = () => {
    if (isUpdatable(item)) {
      onClick(item)
    }
  }

  const handleMainReportClick = async () => {
    const {id} = item
    try {
      await finalizeEegSummary(id)
      onSearch({...query})
    } catch (err) {
      handleFailureModalOpen(err.message)
    }
  }

  let idnAnalId = ''
  if (item.ecIndividualId != null && item.eoIndividualId != null) {
    idnAnalId = `EC: ${item.ecIndividualId ?? '-'}\nEO: ${
      item.eoIndividualId ?? '-'
    }`
  } else if (item.ecIndividualId != null && item.eoIndividualId == null) {
    idnAnalId = `EC: ${item.ecIndividualId ?? '-'}`
  } else if (item.ecIndividualId == null && item.eoIndividualId != null) {
    idnAnalId = `EO: ${item.eoIndividualId ?? '-'}`
  } else {
    idnAnalId = '-'
  }

  let reAnalId = ''
  if (item.ecReAnalysisId != null && item.eoReAnalysisId != null) {
    reAnalId = `EC: ${item.ecReAnalysisId ?? '-'}\nEO: ${
      item.eoReAnalysisId ?? '-'
    }`
  } else if (item.ecReAnalysisId != null && item.eoReAnalysisId == null) {
    reAnalId = `EC: ${item.ecReAnalysisId ?? '-'}`
  } else if (item.ecReAnalysisId == null && item.eoReAnalysisId != null) {
    reAnalId = `EO: ${item.eoReAnalysisId ?? '-'}`
  } else {
    reAnalId = '-'
  }

  let tfileName = ''
  if (item.ecFileName != null && item.eoFileName != null) {
    tfileName = `EC: ${item.ecFileName ?? '-'}\nEO: ${item.eoFileName ?? '-'}`
  } else if (item.ecFileName != null && item.eoFileName == null) {
    tfileName = `EC: ${item.ecFileName ?? '-'}`
  } else if (item.ecFileName == null && item.eoFileName != null) {
    tfileName = `EO: ${item.eoFileName ?? '-'}`
  } else {
    tfileName = '-'
  }

  const renderEyeStatus = (item: SearchedIndividualSummary) => {
    if (item.eoIndividualId && item.ecIndividualId) return t('IECEO')
    if (item.eoIndividualId) return t('IEO')
    if (item.ecIndividualId) return t('IEC')
    return '-'
  }

  return (
    <TableRow hover onClick={handleClick}>
      <TableCell align='center' padding='none'>
        <Checkbox
          checked={checked}
          disabled={!isUpdatable(item)}
          inputProps={{'aria-labelledby': labelId}}
          size='small'
        />
      </TableCell>
      <TableCell align='center' padding='none'>
        {item.id}
      </TableCell>
      <TableCell align='center' padding='none'>
        {idnAnalId}
      </TableCell>
      <TableCell align='center' padding='none'>
        {reAnalId}
      </TableCell>
      <TableCellReport
        align='center'
        padding='none'
        reportId={item.id}
        isMainReport={
          isDefined(item.finalAnalysisYn) && item.finalAnalysisYn === 'Y'
        }
        onClick={handleMainReportClick}
      >
        {item.report}
      </TableCellReport>
      <TableCell align='center' padding='none'>
        <ClientName
          firstName={item.firstName}
          lastName={item.lastName}
          birth={item.birth}
          gender={item.gender}
        />
      </TableCell>
      <TableCell align='center' padding='none'>
        {renderEyeStatus(item)}
      </TableCell>
      <TableCell align='center' padding='none' style={{whiteSpace: 'pre-line'}}>
        {tfileName}
      </TableCell>
      <TableCell align='center' padding='none'>
        {isoStringToDateAndTimeString(String(item.measureDate))}
      </TableCell>
      <TableCell align='center' padding='none'>
        {item.normdbVersion || '-'}
      </TableCell>
      <TableCell align='center' padding='none'>
        {t(statusToI18nString(item.progress as AnalysisStatus))}
      </TableCell>
      <TableCell align='center' padding='none'>
        {t(
          statusToI18nStringPremium(
            item.additionalAnalysis,
            item.progress,
            item.reportType,
          ),
        )}
      </TableCell>
      <TableCell align='center' padding='none'>
        <TableButton
          onClick={handleResultClick}
          disabled={item.progress !== 'SUCCESS'}
        >
          {t('IRead')}
        </TableButton>
      </TableCell>
    </TableRow>
  )
}

function PageTable({isShowSummaryButton}: {isShowSummaryButton: boolean}) {
  const {t} = useTranslation()
  const classes = useStyles()

  const {loading, query, paging, pagingInfo, items, onSearch} =
    useEegSummarySearch()

  const [selectedId, setSelectedId] = useState<number>(0)

  const handleRowClick = (item: SearchedIndividualSummary) => {
    setSelectedId(item.id)
  }

  const isRowSelected = (item: SearchedIndividualSummary) =>
    selectedId === item.id

  const emptyRows = items === null ? 5 : 5 - items.length
  //
  const setPageIndex = (page: number) =>
    onSearch({
      ...query,
      paging: {
        page,
        size: paging.size,
      },
    })

  const onSizeChanged = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    const size = parseInt(event.target.value as string, 10)
    onSearch({
      ...query,
      paging: {
        page: 0,
        size,
      },
    })
  }

  return (
    <TableContainer className={classes.tableContainer}>
      <TableToolbar
        selectedId={selectedId}
        isShowSummaryButton={isShowSummaryButton}
      />
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.tableHeader}>
            <TableCell align='center' padding='none' />
            <TableCell align='center' padding='none'>
              {t('IAnalysisNo')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IEEGIndividualIndex')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IReAnalysis_no')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IMainReport')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IPatientName')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IECEO')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IFileName')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IMeasureDate')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IVersion')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IProgress')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IPremium')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IAnalysisReport')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <RenderTableRow
              key={item.id}
              item={item}
              onClick={handleRowClick}
              isSelected={isRowSelected}
            />
          ))}
          {emptyRows > 0 && (
            <TableRow
              style={{height: 43 * emptyRows, backgroundColor: '#F9F9FB'}}
            >
              <TableCell colSpan={14} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Pagination
        totalPageCount={pagingInfo.totalPages}
        currentPageIndex={query.paging.page ?? 0}
        itemCountPerPage={query.paging.size ?? 10}
        setCurrentPageIndex={setPageIndex}
        loading={loading}
        onItemCountPerPageChanged={onSizeChanged}
      />
    </TableContainer>
  )
}

export default PageTable
